import { useEffect, useState } from 'react'

/**
 * The ticking match minute, anchored on the server's clock.
 *
 * Shared by the backoffice live-matches panel and the gameweek grid's live cards. There is exactly
 * one of these, because a second would drift against the first.
 *
 * Both poll every ~10s, and a minute that jumps every ten seconds reads as a broken clock, so the
 * displayed minute advances locally from `minute + (localNow − fetchedAt)` and resyncs to the
 * server's number on every poll.
 *
 * Two rules keep it honest:
 *
 * - **Never tick past a plausible bound.** Freeze at 45'/90' plus generous stoppage rather than
 *   showing 97' because a poll was missed. A clock that keeps counting is worse than one that
 *   stops.
 * - **`halftime` does not tick at all.** It shows "HT", not a running number.
 */

/** Generous stoppage, and the point past which a ticking number is a guess rather than a reading. */
export const FIRST_HALF_CEILING = 45 + 10
export const SECOND_HALF_CEILING = 90 + 15

export interface TickingClock {
  /** Milliseconds since the payload's `serverTime`, ticking locally. */
  elapsedMs: number
}

/**
 * The offset between the payload's `serverTime` and this browser's clock, re-established on every
 * poll. Nothing here reads `Date.now()` as an absolute — a skewed browser clock shifts both terms
 * of the subtraction identically and cancels out.
 */
export function useMatchClock(serverTime: string | null, tickMs = 1_000): TickingClock {
  const [elapsedMs, setElapsedMs] = useState(0)

  useEffect(() => {
    if (serverTime === null) return
    // The anchor is re-established on every poll. `elapsedMs` is deliberately *not* reset here:
    // the first tick (a second later) recomputes it against the new anchor, and at a 10s poll
    // cadence the stale value is under a minute — which is below the resolution anything reading
    // this renders. Resetting it synchronously would be a setState inside an effect body.
    const anchoredAt = Date.now()
    const id = setInterval(() => setElapsedMs(Date.now() - anchoredAt), tickMs)
    return () => clearInterval(id)
  }, [serverTime, tickMs])

  return { elapsedMs }
}

/**
 * What to render in the minute slot.
 *
 * Returns a string because the answer is not always a number: half time is "HT", a match with no
 * reported minute is "—", and a frozen clock is `90+` rather than a fabricated 97.
 */
export function displayMinute(
  status: string,
  minute: number | null,
  elapsedMs: number
): string {
  if (status === 'halftime') return 'HT'
  if (status === 'finished') return 'FT'
  if (status !== 'live') return '—'
  if (minute === null) return '—'

  const ticked = minute + Math.floor(elapsedMs / 60_000)
  // Which half we are in decides the bound: a 44' that has drifted to 47' is stoppage time, not
  // a second-half minute, and pretending otherwise is exactly the fabrication being avoided.
  const ceiling = minute <= 45 ? FIRST_HALF_CEILING : SECOND_HALF_CEILING
  if (ticked >= ceiling) return `${ceiling === FIRST_HALF_CEILING ? 45 : 90}+`
  return `${ticked}'`
}

/** "in 2d 3h", "in 14m", "now" — the countdown beside a deadline or a next kickoff. */
export function countdown(targetIso: string, fromIso: string, elapsedMs: number): string {
  const remaining = Date.parse(targetIso) - (Date.parse(fromIso) + elapsedMs)
  if (Number.isNaN(remaining)) return ''
  if (remaining <= 0) return 'passed'
  const minutes = Math.floor(remaining / 60_000)
  const days = Math.floor(minutes / (60 * 24))
  const hours = Math.floor((minutes % (60 * 24)) / 60)
  if (days > 0) return `in ${days}d ${hours}h`
  if (hours > 0) return `in ${hours}h ${minutes % 60}m`
  return `in ${minutes}m`
}
